import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { HaNContext, HaNSearchContext, type HereAndNowTab } from "@lib/logic";
import { useRequiredContext } from "@lib/utils";
import { cn } from "@utils";

import { useHaNTabLinkSearch } from "../data/useHaNBuilding";
import { useAvailableTabs } from "../useHaNTabs";

const LABEL_KEY_BY_TAB = {
  rooms: "hereAndNow:Rooms",
  workspaces: "hereAndNow:Desks",
  people: "hereAndNow:People",
  facilities: "hereAndNow:Facilities",
} as const satisfies Record<HereAndNowTab, string>;

export const HaNTabs = () => {
  const { tab, mapViewApiRef } = useRequiredContext(HaNContext);
  const { isSearching } = useRequiredContext(HaNSearchContext);
  const availableTabs = useAvailableTabs();
  const search = useHaNTabLinkSearch();

  if (isSearching) return null;

  const fitFloorInView = () => {
    mapViewApiRef.current?.fitBuildingNodes({ nodeIds: [], abortIfAlreadyInView: true });
  };

  return (
    <nav className="flex h-[60px] justify-around gap-x-4 px-5">
      {availableTabs.map(availableTab => (
        <HaNTabLink
          key={availableTab}
          tab={availableTab}
          search={search}
          onClick={availableTab === tab ? undefined : fitFloorInView}
        />
      ))}
    </nav>
  );
};

const HaNTabLink = ({ tab, search, onClick }: { tab: HereAndNowTab; search: string; onClick?: () => void }) => {
  const { t } = useTranslation();

  return (
    <NavLink
      to={{ pathname: `/now/${tab}`, search }}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          "flex flex-1 items-center justify-center border-b-2 border-transparent font-normal text-mapiq-black-500 no-underline hover:text-mapiq-black-800* hover:no-underline focus-visible:text-mapiq-black-800* focus-visible:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mapiq-black-900",
          { "border-mapiq-black-800* font-semibold text-mapiq-black-800*": isActive },
        )
      }>
      {t(LABEL_KEY_BY_TAB[tab])}
    </NavLink>
  );
};
